import styled, { css, keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

const loadingSlide = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(320%); }
`;

export const Page = styled.div`
  padding: 32px 0 52px;

  @media (max-width: 768px) {
    padding: 34px 14px 60px;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 16px;

  @media (max-width: 680px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 20px;
  }
`;

export const DashboardNavRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 14px;

  @media (max-width: 900px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const Eyebrow = styled.span`
  display: block;
  color: #2f67d8;
  font: 12px rebondG-Semi-Bold;
  letter-spacing: 1.1px;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

export const Title = styled.h1`
  color: #0f172a;
  font: 38px/1.08 rebondG-Bold;
  letter-spacing: -1px;
  margin: 0 0 8px;

  @media (max-width: 520px) { font-size: 30px; }
`;

export const Subtitle = styled.p`
  color: #64748b;
  font: 15px/1.6 rebondG-Regular;
  margin: 0;
`;

export const Tabs = styled.div`
  align-items: center;
  background: #f1f5f9;
  border: 1px solid #e7edf4;
  border-radius: 13px;
  display: flex;
  flex: 0 0 auto;
  padding: 4px;
`;

export const Tab = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 9px;
  color: #64748b;
  cursor: pointer;
  display: flex;
  font: 13px rebondG-Semi-Bold;
  gap: 8px;
  padding: 10px 16px;
  transition: all .18s ease;

  ${({ $active }) => $active && css`
    background: #fff;
    box-shadow: 0 2px 8px rgba(15, 23, 42, .08);
    color: #173f91;
  `}
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1000px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { gap: 10px; }
`;

export const StatCard = styled.article`
  animation: ${fadeUp} .35s ease both;
  animation-delay: ${({ $index }) => $index * 45}ms;
  background: #fff;
  border: 1px solid #e8edf4;
  border-radius: 18px;
  box-shadow: 0 5px 22px rgba(15, 23, 42, .05);
  min-height: 170px;
  overflow: hidden;
  padding: 22px;
  position: relative;

  &::after {
    background: ${({ $color }) => $color};
    border-radius: 999px;
    content: '';
    height: 4px;
    left: 22px;
    position: absolute;
    right: 22px;
    top: 0;
  }

  @media (max-width: 480px) {
    min-height: 150px;
    padding: 17px;
  }
`;

export const StatIcon = styled.div`
  align-items: center;
  background: ${({ $color }) => `${$color}12`};
  border-radius: 11px;
  color: ${({ $color }) => $color};
  display: flex;
  height: 40px;
  justify-content: center;
  margin-bottom: 19px;
  width: 40px;
`;

export const StatValue = styled.div`
  color: #0f172a;
  font: 40px/1 rebondG-Bold;
  letter-spacing: -.6px;
  margin-bottom: 8px;
`;

export const StatLabel = styled.div`
  color: #64748b;
  font: 16px/1.35 rebondG-Semi-Bold;
`;

export const BusinessSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  flex: 1;
  margin-bottom: ${({ $inline }) => $inline ? "0" : "20px"};

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

export const SummaryCard = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #fff 0%, ${({ $tone }) => $tone || "#f8fafc"} 100%);
  border: 1px solid ${({ $accent }) => $accent ? `${$accent}20` : "#e5ebf3"};
  border-radius: 15px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .03), 0 7px 20px rgba(15, 23, 42, .045);
  display: flex;
  gap: 13px;
  overflow: hidden;
  padding: ${({ $compact }) => $compact ? "10px 14px" : "15px 17px"};
  position: relative;
  transition: transform .18s ease, box-shadow .18s ease;

  &::before {
    background: ${({ $accent }) => $accent || "#2f67d8"};
    bottom: 12px;
    content: '';
    left: 0;
    position: absolute;
    top: 12px;
    width: 3px;
  }

  &::after {
    background: ${({ $accent }) => $accent || "#2f67d8"};
    border-radius: 50%;
    content: '';
    height: 72px;
    opacity: .045;
    position: absolute;
    right: -28px;
    top: -32px;
    width: 72px;
  }

  &:hover {
    box-shadow: 0 3px 6px rgba(15, 23, 42, .04), 0 12px 28px rgba(15, 23, 42, .075);
    transform: translateY(-2px);
  }
`;

export const SummaryIcon = styled.div`
  align-items: center;
  background: linear-gradient(145deg, ${({ $accent }) => $accent || "#2f67d8"}, ${({ $accent }) => $accent ? `${$accent}d9` : "#1d4fae"});
  border-radius: 10px;
  box-shadow: 0 5px 12px ${({ $accent }) => $accent ? `${$accent}28` : "rgba(47, 103, 216, .16)"};
  color: #fff;
  display: flex;
  height: 38px;
  justify-content: center;
  width: 38px;
`;

export const SummaryValue = styled.strong`
  color: #0f172a;
  display: block;
  font: 24px/1 rebondG-Bold;
  letter-spacing: -.35px;
  margin-bottom: 5px;
`;

export const SummaryLabel = styled.span`
  color: #64748b;
  font: 12px rebondG-Medium;
`;

export const BusinessLayout = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr);
`;

export const Panel = styled.section`
  background: #fff;
  border: 1px solid #e5ebf3;
  border-radius: 18px;
  box-shadow: 0 6px 28px rgba(15, 23, 42, .055);
  overflow: hidden;
`;

export const PanelHeader = styled.div`
  border-bottom: 1px solid #eef2f7;
  padding: 20px;
`;

export const DirectoryHeader = styled(PanelHeader)`
  align-items: center;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  padding: 13px 16px;

  @media (max-width: 680px) {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }
`;

export const DirectoryTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

export const DirectoryIcon = styled.div`
  align-items: center;
  background: #eef4ff;
  border-radius: 11px;
  color: #2f67d8;
  display: flex;
  flex: 0 0 36px;
  height: 36px;
  justify-content: center;
`;

export const DirectoryCount = styled.span`
  background: #f1f5f9;
  border-radius: 999px;
  color: #64748b;
  font: 11px rebondG-Semi-Bold;
  margin-left: 7px;
  padding: 5px 9px;
  vertical-align: 2px;
`;

export const PanelTitle = styled.h2`
  color: #0f172a;
  font: 17px rebondG-Bold;
  margin: 0 0 4px;
`;

export const PanelMeta = styled.p`
  color: #94a3b8;
  font: 12px rebondG-Regular;
  margin: 0;
`;

export const SearchBox = styled.label`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 11px;
  color: #94a3b8;
  display: flex;
  gap: 9px;
  margin-top: 14px;
  padding: 0 12px;

  &:focus-within { border-color: #8eb1f5; box-shadow: 0 0 0 3px #2f67d811; }
`;

export const DirectorySearch = styled(SearchBox)`
  flex: 0 1 340px;
  margin: 0;
  width: 340px;

  @media (max-width: 680px) {
    flex-basis: auto;
    width: 100%;
  }
`;

export const SearchInput = styled.input`
  background: transparent;
  border: 0;
  color: #0f172a;
  flex: 1;
  font: 13px rebondG-Medium;
  height: 40px;
  min-width: 0;
  outline: 0;

  &::placeholder { color: #a7b2c1; }
`;

export const BusinessList = styled.div`
  background: linear-gradient(180deg, #fbfdff 0%, #f8fafc 100%);
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 10px 14px 12px;
  scroll-snap-type: x proximity;

  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-track { background: #f5f7fa; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 999px; }
`;

export const BusinessItem = styled.button`
  background: ${({ $active }) => $active ? "linear-gradient(135deg, #f0f6ff 0%, #fff 100%)" : "#fff"};
  border: 1px solid ${({ $active }) => $active ? "#bcd3fb" : "#e7edf4"};
  border-radius: 13px;
  box-shadow: ${({ $active }) => $active
    ? "0 7px 18px rgba(47, 103, 216, .11)"
    : "0 2px 8px rgba(15, 23, 42, .035)"};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0;
  flex: 0 0 calc((100% - 30px) / 4);
  min-height: 92px;
  overflow: hidden;
  padding: 12px;
  position: relative;
  scroll-snap-align: start;
  text-align: left;
  transition: all .16s ease;
  width: 100%;

  &::before {
    background: linear-gradient(180deg, #2f67d8, #6f9df5);
    border-radius: 0 5px 5px 0;
    bottom: 14px;
    content: '';
    left: 0;
    opacity: ${({ $active }) => $active ? 1 : 0};
    position: absolute;
    top: 14px;
    transform: scaleY(${({ $active }) => $active ? 1 : .35});
    transition: all .18s ease;
    width: 3px;
  }

  &:hover {
    background: linear-gradient(135deg, #f8fbff, #fff);
    border-color: #cbdcf5;
    box-shadow: 0 7px 18px rgba(15, 23, 42, .07);
    transform: translateY(-2px);

    &::before { opacity: .65; transform: scaleY(1); }
  }

  @media (max-width: 1100px) { flex-basis: calc((100% - 20px) / 3); }
  @media (max-width: 780px) { flex-basis: calc((100% - 10px) / 2); }
  @media (max-width: 500px) { flex-basis: 100%; }
`;

export const BusinessItemTop = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`;

export const BusinessAvatar = styled.div`
  align-items: center;
  background: ${({ $active }) => $active
    ? "linear-gradient(145deg, #3e79e8, #2458bd)"
    : "linear-gradient(145deg, #f2f5f9, #e7edf4)"};
  border-radius: 10px;
  box-shadow: ${({ $active }) => $active
    ? "0 5px 12px rgba(47, 103, 216, .22)"
    : "inset 0 0 0 1px #e4eaf1"};
  color: ${({ $active }) => $active ? "#fff" : "#64748b"};
  display: flex;
  flex: 0 0 38px;
  height: 38px;
  justify-content: center;
`;

export const BusinessName = styled.strong`
  color: #172033;
  display: block;
  font: 14px rebondG-Semi-Bold;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const BusinessEmail = styled.span`
  color: #94a3b8;
  display: block;
  font: 11px rebondG-Regular;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ItemStats = styled.div`
  color: #64748b;
  display: flex;
  font: 11px rebondG-Medium;
  gap: 14px;
  margin: 11px 0 0 48px;

  span {
    align-items: center;
    background: #f5f8fc;
    border: 1px solid #edf1f6;
    border-radius: 999px;
    display: flex;
    gap: 5px;
    padding: 3px 7px;
  }
`;

export const LearnerHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  background: linear-gradient(135deg, #fbfdff 0%, #f8fbff 100%);
  border-bottom: 1px solid #edf2f7;
  padding: 12px 18px;

  @media (max-width: 540px) { align-items: flex-start; flex-direction: column; }
`;

export const LearnerIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

export const LearnerBusinessIcon = styled.div`
  align-items: center;
  background: #2f67d8;
  border-radius: 11px;
  box-shadow: 0 6px 16px rgba(47, 103, 216, .2);
  color: #fff;
  display: flex;
  flex: 0 0 38px;
  height: 38px;
  justify-content: center;
`;

export const TableLoadingBar = styled.div`
  background: #eaf1fd;
  height: 2px;
  overflow: hidden;

  &::after {
    animation: ${loadingSlide} .9s ease-in-out infinite;
    background: #2f67d8;
    content: '';
    display: block;
    height: 100%;
    width: 32%;
  }
`;

export const SelectedBadge = styled.span`
  background: #eef4ff;
  border-radius: 999px;
  color: #2f67d8;
  font: 11px rebondG-Semi-Bold;
  padding: 6px 10px;
`;

export const State = styled.div`
  align-items: center;
  color: ${({ $error }) => $error ? "#b91c1c" : "#94a3b8"};
  display: flex;
  flex-direction: column;
  font: 13px/1.5 rebondG-Medium;
  gap: 10px;
  justify-content: center;
  min-height: 260px;
  padding: 35px;
  text-align: center;
`;

export const RetryButton = styled.button`
  align-items: center;
  background: #fff;
  border: 1px solid #d6e1f3;
  border-radius: 9px;
  color: #2f67d8;
  cursor: pointer;
  display: flex;
  font: 12px rebondG-Semi-Bold;
  gap: 7px;
  padding: 8px 12px;
`;

export const Skeleton = styled.div`
  background: linear-gradient(90deg, #f1f5f9 25%, #fafcff 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  border-radius: 11px;
  height: ${({ $height }) => $height || "64px"};
  margin: 9px;
  animation: ${shimmer} 1.25s linear infinite;
`;

export const BusinessSkeleton = styled(Skeleton)`
  flex: 0 0 calc((100% - 30px) / 4);
  margin: 0;

  @media (max-width: 1100px) { flex-basis: calc((100% - 20px) / 3); }
  @media (max-width: 780px) { flex-basis: calc((100% - 10px) / 2); }
  @media (max-width: 500px) { flex-basis: 100%; }
`;
